import {
  faCalendarPlus,
  faCheck,
  faClock,
  faLocationDot,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { RawDraftContentState } from 'draft-js';
import { useMutation } from 'urql';
import { client } from '~/client';
import { hasDisplayableAttributes } from '~/components/AttributeView';
import { Files } from '~/components/FileLink';
import { Editor } from '~/components/form/editor/Editor';
import { ReadOnlyEditor } from '~/components/form/editor/ReadOnlyEditor';
import { deserializeNotes } from '~/components/job-template/JobTemplateForm';
import { TagCombobox, TagEntityType } from '~/components/TagCombobox';
import { useTags } from '~/components/TagCombobox/tags';
import {
  AddTagsButton,
  TagsBuilder,
} from '~/components/TagCombobox/TagsBuilder';
import { redirectNewWindowToLocation } from '~/components/ui/Address';
import { Attributes } from '~/components/ui/Attributes';
import { Avatar } from '~/components/ui/Avatar';
import { BuildingPlaceholder } from '~/components/ui/BuildingPlaceholder';
import { Tooltip } from '~/components/ui/Tooltip';
import { useResetTaskStatusMutation } from '~/generated/graphql';
import { graphql } from '~/gql';
import { JobStatus } from '~/gql/graphql';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { useScrollToFocus } from '~/hooks/useScrollToFocus';
import { formatLocalDate } from '../../formatDate';
import { CompleteCustomTaskDocument } from '../../tasks/Custom';
import { MemoGallery } from '../Feed/Gallery';
import { Job } from '../JobView';
import { TaskList } from '../TaskList';
import { ToggleTaskStatusFn } from '../TaskList/TaskListItem';
import { BookingSection } from './BookingSection';
import { CollectAttributes } from './CollectAttributes';
import { ContactSection } from './ContactSection';
import { getAddress, getContactsAssociatedToJob } from './utils';

export type Contact = {
  id: string;
  name: string;
  email?: string | null;
  image?: string | null;
  phone?: string | null;
};

type WhatProps = {
  job: Job;
  onPlaceNameClick: () => void;
};

type WhoProps = {
  job: Job;
  contacts: Contact[];
};

const JobView_AddTagsToJobMutation = graphql(/* GraphQL */ `
  mutation AddTagsToJob($id: ID!, $tagIds: [ID!]!) {
    addTagsToJob(id: [$id], tagIds: $tagIds) {
      id
      jobTags {
        id
      }
    }
  }
`);

const JobView_RemoveTagsFromJobMutation = graphql(/* GraphQL */ `
  mutation RemoveTagsFromJob($id: ID!, $tagIds: [ID!]!) {
    removeTagsFromJob(id: [$id], tagIds: $tagIds) {
      id
      jobTags {
        id
      }
    }
  }
`);

const JobView_UpdateJobAssigneeNotesMutation = graphql(/* GraphQL */ `
  mutation UpdateJobAssigneeNotes($id: ID!, $assigneeNotes: String) {
    updateJobAssigneeNotes(id: $id, assigneeNotes: $assigneeNotes) {
      id
      assigneeNotes
    }
  }
`);

function Bubble({ children }: { children: ReactNode }) {
  const { isMobile } = useBreakpoint();

  return (
    <div
      className={classNames(
        'flex flex-col leading-5 text-[#002727]',
        isMobile ? 'gap-2' : 'gap-[13px]'
      )}
    >
      {children}
    </div>
  );
}
type SectionProps = {
  children: ReactNode;
  className?: string;
};

function Section({ children, className }: SectionProps) {
  return (
    <div
      className={classNames(
        'flex items-center rounded-[5px] border border-outline-variant bg-white p-4',
        className
      )}
    >
      {children}
    </div>
  );
}

export function WhereBubble({ job, onPlaceNameClick }: WhatProps) {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const { isMobile } = useBreakpoint();
  const site = job.location?.__typename === 'Site' ? job.location : undefined;
  const address = getAddress(site?.address || '');

  return (
    address && (
      <Bubble>
        <Section
          className={classNames(isMobile ? 'gap-2 px-[13px]' : 'gap-[17px]')}
        >
          {job.site?.image ? (
            <Avatar
              className='h-[75px] w-[75px] rounded-[3px]'
              name={job.location?.name}
              image={job.site?.image}
              size='full'
              square
            />
          ) : (
            <BuildingPlaceholder className='!h-[75px] !w-[75px] rounded-[3px] px-0 text-4xl' />
          )}

          <div className='leading-[17.5px]'>
            <p
              className='cursor-pointer font-semibold leading-[17.5px] hover:underline'
              onClick={() => onPlaceNameClick()}
            >
              {job.location?.name}
            </p>
            <p className='text-[#506262]'>{address?.address}</p>
            <p className='text-[#506262]'>{address?.long}</p>
          </div>

          {site?.address && (
            <div className='ml-auto'>
              {!isMobile && (
                <Tooltip isVisible={isTooltipVisible}>Get Direction</Tooltip>
              )}
              <button
                className={classNames(
                  'h-[42px] w-[42px] rounded-full',
                  isMobile ? 'bg-grey-3' : 'hover:bg-grey-3'
                )}
                onClick={() => redirectNewWindowToLocation(site.address || '')}
                onMouseEnter={() => setIsTooltipVisible(true)}
                onMouseLeave={() => setIsTooltipVisible(false)}
              >
                <FontAwesomeIcon
                  color='#506262'
                  className='text-lg'
                  icon={faLocationDot}
                />
              </button>
            </div>
          )}
        </Section>

        <BookingSection job={job} />
      </Bubble>
    )
  );
}

type AddToCalendarButtonProps = {
  jobId: string;
  isIconMode: boolean;
};

function AddToCalendarButton({ jobId, isIconMode }: AddToCalendarButtonProps) {
  const { isMobile } = useBreakpoint();

  return (
    <>
      {/* Large screen */}
      <a
        className={classNames(
          'ml-auto hidden w-fit items-center gap-2 rounded-full border border-outline-variant py-[5px] text-sm font-medium text-[#506262]',
          !isIconMode && 'md:flex lg:hidden xl:flex'
        )}
        href={`/api/calendar/jobs/${jobId}`}
      >
        <FontAwesomeIcon icon={faCalendarPlus} className='ml-2' />
        <p className='mr-[11px]'>Add to calendar</p>
      </a>

      {/* Mobile */}
      <a
        className={classNames(
          'ml-auto flex h-[42px] w-[42px] items-center justify-center rounded-full',
          !isIconMode && 'sm:flex md:hidden lg:flex xl:hidden',
          isMobile ? 'bg-grey-3' : 'hover:bg-grey-3'
        )}
        href={`/api/calendar/jobs/${jobId}`}
      >
        <FontAwesomeIcon
          color='#506262'
          className='text-lg'
          icon={faCalendarPlus}
        />
      </a>
    </>
  );
}

export function WhenBubble({ job }: { job: Job }) {
  const { isMobile } = useBreakpoint();
  const { pathname } = useLocation();

  const startAt = formatLocalDate(job, 'scheduledStart');
  const endAt = formatLocalDate(job, 'scheduledEnd');
  const isOrientedVertically =
    pathname.includes('calendar') ||
    pathname.includes('tasks') ||
    pathname.includes('sites');

  return !startAt && !endAt ? null : (
    <Bubble>
      <Section className={classNames(isMobile && 'px-[13px]')}>
        <div
          className={classNames(
            'flex flex-col !items-start gap-[13px]',
            !isOrientedVertically &&
              'md:flex-row md:gap-5 lg:flex-col lg:gap-[13px] xl:flex-row xl:gap-5'
          )}
        >
          {startAt && (
            <div className='flex flex-col gap-[5px]'>
              <div className='flex items-center gap-[5px] font-semibold leading-[17.5px]'>
                <FontAwesomeIcon color='#005959' size='sm' icon={faClock} />
                Scheduled Start
              </div>
              <p className='font-normal text-[#506262]'>{startAt}</p>
            </div>
          )}

          {endAt && (
            <div className='flex flex-col gap-[5px]'>
              <div className='flex items-center gap-[5px] font-semibold leading-[17.5px]'>
                <FontAwesomeIcon color='#005959' size='sm' icon={faClock} />
                Scheduled End
              </div>
              <p className='font-normal text-[#506262]'>{endAt}</p>
            </div>
          )}
        </div>
        <AddToCalendarButton jobId={job.id} isIconMode={isOrientedVertically} />
      </Section>
    </Bubble>
  );
}

export function WhoBubble({ job, contacts }: WhoProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { owners, assignees, otherAssignees } = getContactsAssociatedToJob(
    contacts,
    job
  );

  const openContactModal = (id: string) => {
    const trimmedPath = pathname.replace(/\/$/, ''); // Removes trailing slash if present
    const regex = /\/tasks\/\d+/;

    const url = regex.test(trimmedPath)
      ? trimmedPath.replace(regex, `/contacts/${id}`)
      : `${trimmedPath}/contacts/${id}`;
    navigate(url);
  };

  return (
    <Bubble>
      {owners && owners.length > 0 && (
        <ContactSection
          label={t('jobOwner', { count: owners.length })}
          contacts={owners}
          onContactClick={openContactModal}
        />
      )}

      {assignees && assignees.length > 0 && (
        <ContactSection
          label='Assigned to'
          contacts={assignees}
          onContactClick={openContactModal}
        />
      )}

      {otherAssignees.length > 0 && (
        <ContactSection
          label='Includes'
          contacts={otherAssignees}
          onContactClick={openContactModal}
        />
      )}
    </Bubble>
  );
}

type ExternalAssigneeBubbleProps = {
  shareAttributes?: boolean;
  shareJobHistory?: boolean;
  shareDeploymentHistory?: boolean;
};

export function ExternalAssigneeBubble({
  shareAttributes = true,
  shareJobHistory = true,
  shareDeploymentHistory = false,
}: ExternalAssigneeBubbleProps) {
  const { isMobile } = useBreakpoint();

  // Build the list of shared items based on actual settings
  const sharedItems = [
    { enabled: shareAttributes, label: 'Attributes' },
    { enabled: shareJobHistory, label: 'Job History' },
    { enabled: shareDeploymentHistory, label: 'Deployment History' },
  ].filter((item) => item.enabled);

  // Don't render if nothing is shared
  if (sharedItems.length === 0) {
    return null;
  }

  return (
    <Bubble>
      <Section
        className={classNames('px-[13px]', isMobile ? 'gap-2' : 'gap-4')}
      >
        <div className='w-full'>
          <h4 className='mb-2 text-base font-medium leading-5 text-primary'>
            Share with External Assignee
          </h4>
          <p className='mb-3 text-sm leading-5 text-secondary'>
            If assigned to someone outside your organisation, they can view:
          </p>

          <ul className='flex flex-col gap-2 text-sm leading-5 text-[#002727]'>
            {sharedItems.map((item) => (
              <li key={item.label} className='flex items-center gap-2'>
                <FontAwesomeIcon
                  icon={faCheck}
                  className='h-4 w-4 text-emerald-600'
                  aria-hidden='true'
                />
                <span>{item.label}</span>
              </li>
            ))}
          </ul>

          <p className='mt-3 text-xs leading-[14px] text-tertiary'>
            Team members always have full access to asset details.
          </p>
        </div>
      </Section>
    </Bubble>
  );
}

export function WhatBubble({
  job,
  contacts,
}: {
  job: Job;
  contacts: Contact[];
}) {
  const { t } = useTranslation();
  const { id, jobTags, notes, assigneeNotes, attachments } = job;
  const [, completeTask] = useMutation(CompleteCustomTaskDocument);
  const [, reset] = useResetTaskStatusMutation();
  const [allTags] = useTags(TagEntityType.Job);
  const tagRef = useScrollToFocus();
  const { isMobile } = useBreakpoint();

  const [isTagBoxOpen, setIsTagBoxOpen] = useState(false);
  const [selectedTagsIds, setSelectedTagsIds] = useState<string[]>(
    jobTags?.map((t) => t.id) || []
  );

  // useCallback: keeps parser stable for memoized callers and logs context
  const parseNotesSafely = useCallback(
    (
      value: string | RawDraftContentState | null | undefined,
      label: 'notes' | 'assigneeNotes'
    ): RawDraftContentState | null => {
      if (!value) {
        return null;
      }

      if (typeof value !== 'string') {
        return value as RawDraftContentState;
      }

      try {
        return deserializeNotes(value);
      } catch (error) {
        console.error('Failed to deserialize job notes', {
          error,
          jobId: id,
          label,
        });
        return null;
      }
    },
    [id]
  );

  // Assignee notes editing state - only editable when job is InProgress
  // Assignee notes are separate from original job notes - they're added by assignees while completing
  // Stored as JSON string of RawDraftContentState (rich text from draft-js)
  // Initial state comes from server; NO sync effect to prevent jumping during active editing
  const [assigneeNotesContent, setAssigneeNotesContent] =
    useState<RawDraftContentState | null>(() =>
      parseNotesSafely(assigneeNotes, 'assigneeNotes')
    );
  // useMemo: avoid re-deserializing on every render and guard malformed content
  const readOnlyNotesContent = useMemo(
    () => parseNotesSafely(notes, 'notes'),
    [notes, parseNotesSafely]
  );

  // useMemo: same guard for assignee notes, stable per value
  const readOnlyAssigneeNotesContent = useMemo(
    () => parseNotesSafely(assigneeNotes, 'assigneeNotes'),
    [assigneeNotes, parseNotesSafely]
  );
  const [showSavedIndicator, setShowSavedIndicator] = useState(false);
  // Track when the indicator was last shown to enforce minimum display time
  const savedIndicatorShownAtRef = useRef<number | null>(null);
  const savedIndicatorTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Track latest content that needs saving - used to flush pending saves on unmount
  const pendingContentRef = useRef<RawDraftContentState | null>(null);
  // Track the last successfully saved content to detect if content has changed
  const lastSavedContentRef = useRef<RawDraftContentState | null>(null);
  // Track current content in a ref so we can check it in timeout without causing re-renders
  const currentContentRef = useRef<RawDraftContentState | null>(
    parseNotesSafely(assigneeNotes, 'assigneeNotes')
  );
  const isSavingAssigneeNotesRef = useRef(false);
  // Store the latest save function in a ref to avoid dependency issues in cleanup
  const saveAssigneeNotesRef = useRef<
    | ((
        content: RawDraftContentState,
        shouldNotify?: boolean
      ) => Promise<boolean>)
    | null
  >(null);

  // Save assignee notes to the server
  // Save indicator tied to successful mutations so users see confirmation
  const saveAssigneeNotes = useCallback(
    async (content: RawDraftContentState, shouldNotify = true) => {
      isSavingAssigneeNotesRef.current = true;
      try {
        const result = await client
          .mutation(JobView_UpdateJobAssigneeNotesMutation, {
            id,
            assigneeNotes: JSON.stringify(content),
          })
          .toPromise();

        if (result.error) {
          toast.error(result.error.message.replace('[GraphQL] ', ''));
          return false;
        }

        pendingContentRef.current = null;
        if (shouldNotify) {
          // Track the successfully saved content
          lastSavedContentRef.current = content;
          // Clear any pending hide timeout so we start fresh
          if (savedIndicatorTimeoutRef.current) {
            clearTimeout(savedIndicatorTimeoutRef.current);
            savedIndicatorTimeoutRef.current = null;
          }
          savedIndicatorShownAtRef.current = Date.now();
          setShowSavedIndicator(true);
        }
        return true;
      } finally {
        isSavingAssigneeNotesRef.current = false;
      }
    },
    [id]
  );

  // Keep the ref updated with the latest save function
  useEffect(() => {
    saveAssigneeNotesRef.current = saveAssigneeNotes;
  }, [saveAssigneeNotes]);

  // Handle assignee notes change from the Editor component
  // Saves immediately (debounce handled inside Editor)
  const handleAssigneeNotesUpdate = useCallback(
    (rawContent: RawDraftContentState) => {
      setAssigneeNotesContent(rawContent);
      // Track the latest content for potential flush on unmount
      pendingContentRef.current = rawContent;
      // Update ref for timeout checks
      currentContentRef.current = rawContent;

      // Save immediately; Editor already debounces its own onUpdate
      void saveAssigneeNotes(rawContent);
    },
    [saveAssigneeNotes]
  );

  // Flush pending saves on unmount to prevent data loss
  // This effect handles two scenarios:
  // 1. SPA navigation (React Router) - fire-and-forget works since JS context persists
  // 2. Page unload (browser close/refresh) - uses sendBeacon for reliable delivery
  useEffect(() => {
    // Handle actual page unloads (browser close, refresh, external navigation)
    // sendBeacon is designed for this - it queues data to be sent even after page terminates
    const handleBeforeUnload = () => {
      if (pendingContentRef.current) {
        // Use sendBeacon for reliable delivery during page unload
        // This is the only way to guarantee data is sent when the page is closing
        const payload = JSON.stringify({
          query: `mutation UpdateJobAssigneeNotes($id: ID!, $assigneeNotes: String) {
            updateJobAssigneeNotes(id: $id, assigneeNotes: $assigneeNotes) {
              id
              assigneeNotes
            }
          }`,
          variables: {
            id,
            assigneeNotes: JSON.stringify(pendingContentRef.current),
          },
        });

        navigator.sendBeacon(
          '/graphql',
          new Blob([payload], { type: 'application/json' })
        );
        pendingContentRef.current = null;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);

      // Handle SPA navigation (component unmount without page unload)
      // Fire-and-forget is acceptable here since the JS context persists
      // and the Promise will resolve even after component unmounts
      if (
        pendingContentRef.current &&
        saveAssigneeNotesRef.current &&
        !isSavingAssigneeNotesRef.current
      ) {
        // Fire-and-forget: avoid double-saving while an in-flight mutation runs
        saveAssigneeNotesRef.current(pendingContentRef.current, false);
        pendingContentRef.current = null;
      }
    };
  }, [id]);

  // Auto-hide the saved indicator after minimum 3 seconds
  // Ensures it stays visible for at least 3 seconds after the last successful save
  // Timer only resets when showSavedIndicator becomes true (new save), not on content changes
  // If content changes, it's hidden immediately in handleAssigneeNotesUpdate
  useEffect(() => {
    if (!showSavedIndicator) {
      return;
    }

    // Clear any existing timeout since we're setting a new one
    if (savedIndicatorTimeoutRef.current) {
      clearTimeout(savedIndicatorTimeoutRef.current);
    }

    // Always wait 2 seconds from when indicator is shown
    savedIndicatorTimeoutRef.current = setTimeout(() => {
      // Only hide if content still matches what was saved (content changes hide it immediately)
      const contentMatches =
        lastSavedContentRef.current &&
        currentContentRef.current &&
        JSON.stringify(currentContentRef.current) ===
          JSON.stringify(lastSavedContentRef.current);
      if (contentMatches) {
        setShowSavedIndicator(false);
      }
      savedIndicatorTimeoutRef.current = null;
    }, 2000);

    return () => {
      if (savedIndicatorTimeoutRef.current) {
        clearTimeout(savedIndicatorTimeoutRef.current);
        savedIndicatorTimeoutRef.current = null;
      }
    };
  }, [showSavedIndicator]);

  // Determine if notes are editable (only when job is in progress)
  const isNotesEditable = job.status === JobStatus.InProgress;

  // Remove the isTagLoading state that was causing "Loading..." to show during task navigation
  // Tags should be available immediately since they're part of the job data

  const toggleTaskStatus: ToggleTaskStatusFn = async ({ id, checked }) => {
    const { error } = checked
      ? await completeTask({
          input: { id, date: new Date() },
        })
      : await reset({ id });

    if (error) {
      toast.error(error.message.replace('[GraphQL] ', ''));
      return;
    }
  };

  const updateJobTags = async (tagIds: string[]) => {
    const toRemoveTags = jobTags
      ?.map((a) => a.id)
      .filter((id) => !tagIds.includes(id));

    const toAddTags = tagIds.filter(
      (id: string) => !jobTags?.map((a) => a.id).includes(id)
    );

    await removeTagsFromJob(toRemoveTags);
    await addTagsToJob(toAddTags);
  };

  async function addTagsToJob(tagIds: string[] = []) {
    if (tagIds.length) {
      const result = await client.mutation(JobView_AddTagsToJobMutation, {
        id,
        tagIds,
      });
      const { error } = result;

      if (error) {
        toast.error(error.message.replace('[GraphQL] ', ''));
      }
    }
  }

  async function removeTagsFromJob(tagIds: string[] = []) {
    if (tagIds?.length) {
      const result = await client.mutation(JobView_RemoveTagsFromJobMutation, {
        id,
        tagIds,
      });
      const { error } = result;

      if (error) {
        toast.error(error.message.replace('[GraphQL] ', ''));
      }
    }
  }

  const haveTags = jobTags && jobTags.length > 0;
  return (
    <Bubble>
      <Section className='px-[13px]'>
        <div className='w-full overflow-x-hidden'>
          <div className='flex flex-col gap-4'>
            {' '}
            <div ref={tagRef}>
              <label className='text-sm font-medium text-secondary'>Tags</label>
              {haveTags && !isTagBoxOpen ? (
                <TagsBuilder
                  tags={jobTags}
                  onUpdate={() => {
                    setIsTagBoxOpen(true);
                    setSelectedTagsIds(jobTags?.map((t) => t.id));
                  }}
                />
              ) : isTagBoxOpen ? (
                <TagCombobox
                  entityType={TagEntityType.Job}
                  options={allTags}
                  value={allTags.filter((t) => selectedTagsIds.includes(t.id))}
                  onChange={(tags, mode) => {
                    const tagIds = tags.map(({ id }) => id);

                    if (mode === 'create') {
                      updateJobTags(tagIds);
                    } else {
                      setSelectedTagsIds(tagIds);
                    }
                  }}
                  dialogMode={true}
                  isOpen={isTagBoxOpen}
                  onClose={() => setIsTagBoxOpen(false)}
                  onSubmit={() => updateJobTags(selectedTagsIds)}
                  containerStyles={classNames(
                    'w-full',
                    isMobile
                      ? '!max-h-[calc(100vh-100px)]'
                      : '!max-h-[calc(100vh-300px)]'
                  )}
                />
              ) : (
                <AddTagsButton onClick={() => setIsTagBoxOpen(true)} />
              )}
            </div>
            {/* Original job notes (read-only) */}
            {notes && (
              <div className='mb-2 flex flex-col gap-1'>
                <label className='text-sm font-medium text-secondary'>
                  Job Details
                </label>
                <ReadOnlyEditor content={readOnlyNotesContent ?? undefined} />
              </div>
            )}
            {attachments && attachments.length > 0 && (
              <div className='flex flex-col gap-1'>
                <label className='text-sm font-medium text-secondary'>
                  Attachments
                </label>
                <Files attachments={attachments ?? []} />
                <MemoGallery attachments={attachments ?? []} />
              </div>
            )}
          </div>
        </div>
      </Section>
      {(isNotesEditable || assigneeNotes) && (
        <Section className='px-[13px]'>
          {' '}
          {/* Assignee notes - editable when job is in progress */}
          {isNotesEditable ? (
            <div className='flex w-full flex-col gap-1'>
              <label className='text-base font-medium leading-5'>
                {t('myNotes', 'My Notes')}
              </label>
              <Editor
                content={assigneeNotesContent}
                onUpdate={handleAssigneeNotesUpdate}
                placeholder={t('addAssigneeNotes', 'Add notes...')}
                savedIndicator={
                  <div
                    className={classNames(
                      'flex items-center gap-1.5 text-xs text-tertiary transition-opacity duration-300',
                      showSavedIndicator ? 'opacity-100' : 'opacity-0'
                    )}
                    role='status'
                    aria-live='polite'
                  >
                    <FontAwesomeIcon
                      icon={faCheck}
                      className='h-3 w-3 text-emerald-600'
                      aria-hidden='true'
                    />
                    <span>Saved</span>
                  </div>
                }
              />
            </div>
          ) : (
            /* Show assignee notes as read-only when job is not in progress */
            assigneeNotes && (
              <div className='flex w-full flex-col gap-1'>
                <label className='text-sm font-medium text-secondary'>
                  {t('assigneeNotes', 'Assignee Notes')}
                </label>
                <ReadOnlyEditor
                  content={readOnlyAssigneeNotesContent ?? undefined}
                />
              </div>
            )
          )}
        </Section>
      )}

      {job.attributes &&
        job.attributes.length > 0 &&
        hasDisplayableAttributes(job.attributes) && (
          <Section className='px-[13px] '>
            <div className=''>
              <p className='text-lg font-medium leading-5'>Attributes</p>
              <Attributes
                data={{ attributes: job.attributes }}
                contacts={contacts}
              />
            </div>
          </Section>
        )}

      {job.jobAttributeIds && job.jobAttributeIds.length > 0 && (
        <Section className='px-[13px]'>
          <div className='w-full overflow-x-hidden'>
            <p className='mb-4 text-lg font-medium leading-5'>Custom Fields</p>
            <CollectAttributes
              entityType='Job'
              entityId={job.id}
              selections={job.jobAttributeIds}
              values={job.jobAttributes}
              readMode={job.status !== JobStatus.InProgress}
            />
          </div>
        </Section>
      )}

      {job.tasks.length > 0 && (
        <Section className='px-[13px]'>
          <div className='flex w-full flex-col gap-5'>
            <p className='text-lg font-medium leading-5'>Tasks</p>
            <div>
              <TaskList job={job} onToggle={toggleTaskStatus} label={''} />
            </div>
          </div>
        </Section>
      )}
    </Bubble>
  );
}
